import React, { useState, useEffect } from 'react';
import {
  Users,
  BookOpen,
  Activity,
  Plus,
  Trash2,
  AlertCircle,
  Sparkles,
  Check,
  ChevronDown,
  ChevronRight,
  History,
  Play,
  RotateCcw,
  Clock,
  Copy,
  Edit3,
  Eye,
  Save,
  X,
} from 'lucide-react';
import {
  getTerminology,
  saveTerminology,
  deleteTerminology,
  getUsers,
  getLogs,
  getLoginLogs,
  updateUserRole,
  deleteUser,
  TerminologyItem,
  AuditLogEntry,
  SystemPrompt,
  getAllActivePrompts,
  getPromptsForAgent,
  createPromptVersion,
  activatePrompt,
} from '../services/store';
import { User, LANGUAGE_LABELS, LanguageCode } from '../types';
import { supabase } from '../services/supabaseClient';

// Helper function to format relative time
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

// Agent display names and descriptions
const AGENT_INFO: Record<string, { name: string; description: string; color: string }> = {
  journalist: {
    name: 'Journalist Agent',
    description: 'Polishes content, fixes grammar, applies editorial feedback',
    color: 'blue',
  },
  critic: {
    name: 'Critic Agent',
    description: 'Reviews content and identifies issues (ai_fixable vs human_required)',
    color: 'orange',
  },
  critic_translation: {
    name: 'Translation Reviewer',
    description: 'Proofreads translated content for grammar and accuracy',
    color: 'purple',
  },
  social: {
    name: 'Social Media Agent',
    description: 'Generates social media posts for various platforms',
    color: 'pink',
  },
  translator: {
    name: 'Translator Agent',
    description: 'Translates content between Dari, Pashto, and English',
    color: 'green',
  },
};

const Admin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'logs' | 'loginHistory' | 'terms' | 'prompts'>('users');
  const [users, setUsers] = useState<User[]>([]);
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loginLogs, setLoginLogs] = useState<AuditLogEntry[]>([]);
  const [logActionFilter, setLogActionFilter] = useState<string>('all');
  const [logSearch, setLogSearch] = useState('');
  const [loginHistoryUserId, setLoginHistoryUserId] = useState<string>('');
  const [loginHistoryFromDate, setLoginHistoryFromDate] = useState<string>('');
  const [loginHistoryToDate, setLoginHistoryToDate] = useState<string>('');
  const [terms, setTerms] = useState<TerminologyItem[]>([]);
  const [refresh, setRefresh] = useState(0);

  // Terminology Form State
  const [showTermForm, setShowTermForm] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [newTerm, setNewTerm] = useState<Partial<TerminologyItem>>({
    language: 'fa-AF',
    type: 'forbidden'
  });

  // Prompt Management State
  const [activePrompts, setActivePrompts] = useState<SystemPrompt[]>([]);
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [agentVersions, setAgentVersions] = useState<Record<string, SystemPrompt[]>>({});
  const [loadingVersions, setLoadingVersions] = useState<string | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<string | null>(null);
  const [editPromptText, setEditPromptText] = useState('');
  const [editPromptNotes, setEditPromptNotes] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [promptSuccess, setPromptSuccess] = useState<string | null>(null);
  const [viewingPrompt, setViewingPrompt] = useState<SystemPrompt | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (activeTab === 'prompts') {
        const prompts = await getAllActivePrompts();
        setActivePrompts(prompts);
      } else if (activeTab === 'loginHistory') {
        const loginLogsData = await getLoginLogs({
          userId: loginHistoryUserId || undefined,
          fromDate: loginHistoryFromDate || undefined,
          toDate: loginHistoryToDate || undefined,
        });
        setLoginLogs(loginLogsData);
      } else {
        const [u, l, t] = await Promise.all([getUsers(), getLogs(), getTerminology()]);
        setUsers(u);
        setLogs(l);
        // Convert terminology to legacy format
        setTerms(t.map((term: any) => ({
          id: term.id,
          original: term.term_en || '',
          replacement: term.term_dari || term.term_pashto || '',
          language: term.term_dari ? 'fa-AF' : 'ps-AF',
          type: 'standard' as const,
          context: term.notes,
          createdBy: term.created_by,
          createdAt: term.created_at,
        })));
      }
    };
    fetchData();
  }, [refresh, activeTab, loginHistoryUserId, loginHistoryFromDate, loginHistoryToDate]);

  // Load version history for an agent
  const loadAgentVersions = async (agentName: string) => {
    if (expandedAgent === agentName) {
      setExpandedAgent(null);
      return;
    }

    setLoadingVersions(agentName);
    const versions = await getPromptsForAgent(agentName);
    setAgentVersions(prev => ({ ...prev, [agentName]: versions }));
    setExpandedAgent(agentName);
    setLoadingVersions(null);
  };

  // Start editing a prompt (create new version)
  const startEditingPrompt = (prompt: SystemPrompt) => {
    setEditingPrompt(prompt.agent_name);
    setEditPromptText(prompt.prompt_text);
    setEditPromptNotes('');
    setPromptSuccess(null);
  };

  // Save new prompt version
  const savePromptVersion = async (activate: boolean = false) => {
    if (!editingPrompt || !editPromptText.trim()) return;

    setSavingPrompt(true);
    setPromptSuccess(null);

    try {
      const newId = await createPromptVersion(
        editingPrompt,
        editPromptText,
        editPromptNotes || undefined,
        activate
      );

      if (newId) {
        setPromptSuccess(activate ? 'New version created and activated!' : 'New version saved as draft.');
        setEditingPrompt(null);
        setEditPromptText('');
        setEditPromptNotes('');

        // Refresh data
        const prompts = await getAllActivePrompts();
        setActivePrompts(prompts);

        // Reload versions if expanded
        if (expandedAgent) {
          const versions = await getPromptsForAgent(expandedAgent);
          setAgentVersions(prev => ({ ...prev, [expandedAgent]: versions }));
        }
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
    } finally {
      setSavingPrompt(false);
    }
  };

  // Activate a specific version
  const handleActivatePrompt = async (promptId: string, agentName: string) => {
    if (!confirm('Activate this version? The current active version will be deactivated.')) return;

    const success = await activatePrompt(promptId);
    if (success) {
      setPromptSuccess('Prompt version activated!');

      // Refresh data
      const prompts = await getAllActivePrompts();
      setActivePrompts(prompts);

      // Reload versions
      const versions = await getPromptsForAgent(agentName);
      setAgentVersions(prev => ({ ...prev, [agentName]: versions }));
    }
  };

  // --- User Management Handlers ---

  const handleRoleChange = async (userId: string, userName: string, newRole: string) => {
    if (confirm(`Are you sure you want to change ${userName}'s role to ${newRole}?`)) {
      try {
        await updateUserRole(userId, newRole);
        setRefresh(prev => prev + 1);
      } catch (e: any) {
        alert("Failed to update role: " + e.message);
      }
    }
  };

  const handleDeleteUser = async (userId: string, userName: string) => {
    if (confirm(`Are you sure you want to suspend/delete ${userName}? They will no longer be able to access the system.`)) {
      try {
        await deleteUser(userId);
        setRefresh(prev => prev + 1);
      } catch (e: any) {
        alert("Failed to suspend user: " + e.message);
      }
    }
  };

  // --- Terminology Handlers ---

  const handleSaveTerm = async () => {
    setFormError(null);
    if (!newTerm.original || !newTerm.replacement || !newTerm.language) {
      setFormError("Original term, Replacement term, and Language are required.");
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Must be logged in to save terms.");

      await saveTerminology({
        id: crypto.randomUUID(),
        original: newTerm.original,
        replacement: newTerm.replacement,
        language: newTerm.language,
        type: newTerm.type as 'forbidden' | 'standard',
        context: newTerm.context,
      }, user.id);

      setRefresh(prev => prev + 1);
      setShowTermForm(false);
      setNewTerm({ language: 'fa-AF', type: 'forbidden' });
    } catch (e: any) {
      setFormError(e.message);
    }
  };

  const handleDeleteTerm = async (id: string) => {
    if (confirm("Delete this terminology rule?")) {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await deleteTerminology(id, user.id);
        setRefresh(prev => prev + 1);
      }
    }
  };

  const getUserName = (userId?: string) => {
    if (!userId) return 'System';
    const user = users.find(u => u.id === userId);
    return user ? (user.full_name || user.email || 'Unknown') : 'Unknown User';
  };

  const getAgentColor = (agentName: string) => {
    return AGENT_INFO[agentName]?.color || 'slate';
  };

  const filteredLogs = logs.filter(log => {
    const matchesAction = logActionFilter === 'all' ? true : log.action === logActionFilter;
    const search = logSearch.trim().toLowerCase();
    if (!search) return matchesAction;
    const haystack = `${log.action} ${log.user?.full_name || log.user?.email || ''} ${log.entity_type || ''} ${log.entity_id || ''}`.toLowerCase();
    return matchesAction && haystack.includes(search);
  });

  return (
    <div className="p-8 max-w-7xl mx-auto h-full overflow-y-auto animate-fade-in">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">Admin Dashboard</h1>
        <p className="text-zinc-500 dark:text-zinc-400 mt-1">Manage users, view activity, configure terminology, and manage AI prompts.</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl mb-8 w-fit">
        <button
          onClick={() => setActiveTab('users')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'users' ? 'bg-white dark:bg-zinc-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900'}`}
        >
          <Users className="h-4 w-4 mr-2" /> Users
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'logs' ? 'bg-white dark:bg-zinc-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900'}`}
        >
          <Activity className="h-4 w-4 mr-2" /> Activity Logs
        </button>
        <button
          onClick={() => setActiveTab('loginHistory')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'loginHistory' ? 'bg-white dark:bg-zinc-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900'}`}
        >
          <History className="h-4 w-4 mr-2" /> Login History
        </button>
        <button
          onClick={() => setActiveTab('terms')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'terms' ? 'bg-white dark:bg-zinc-700 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900'}`}
        >
          <BookOpen className="h-4 w-4 mr-2" /> Terminology DB
        </button>
        <button
          onClick={() => setActiveTab('prompts')}
          className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'prompts' ? 'bg-white dark:bg-zinc-700 text-purple-600 dark:text-purple-400 shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-900'}`}
        >
          <Sparkles className="h-4 w-4 mr-2" /> AI Prompts
        </button>
      </div>

      {/* Success Message */}
      {promptSuccess && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 p-3 rounded-lg flex items-center justify-between animate-fade-in animate-slide-in-down">
          <div className="flex items-center">
            <Check className="h-4 w-4 mr-2" />
            {promptSuccess}
          </div>
          <button onClick={() => setPromptSuccess(null)} className="text-green-500 hover:text-green-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 shadow-sm overflow-hidden min-h-[400px]">
        {/* USERS TAB */}
        {activeTab === 'users' && (
          <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
            <thead className="bg-zinc-50 dark:bg-zinc-800/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">User</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Role</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Desk</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Last Login</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-zinc-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
              {users.map(user => {
                const isSuspended = user.role === 'suspended';
                return (
                  <tr key={user.id} className={isSuspended ? "bg-zinc-50 dark:bg-zinc-900/50 opacity-60" : ""}>
                    <td className="px-6 py-4 flex items-center">
                      <img
                        src={user.avatar_url || `https://ui-avatars.com/api/?name=${user.full_name || user.email}`}
                        className="h-8 w-8 rounded-full mr-3"
                        alt=""
                      />
                      <span className={`text-sm font-medium ${isSuspended ? 'text-zinc-500 line-through' : 'text-zinc-900 dark:text-zinc-100'}`}>
                        {user.full_name || user.email}
                      </span>
                      {isSuspended && <span className="ml-2 text-xs text-red-500 font-bold">(Suspended)</span>}
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-500">
                       <select
                          value={user.role || 'journalist'}
                          onChange={(e) => handleRoleChange(user.id, user.full_name || user.email || '', e.target.value)}
                          className={`bg-zinc-100 dark:bg-zinc-800 border-none rounded text-xs uppercase font-bold tracking-wider py-1 px-2 cursor-pointer focus:ring-2 focus:ring-blue-500 ${
                            isSuspended ? 'text-red-600' : 'text-zinc-700 dark:text-zinc-300'
                          }`}
                        >
                          <option value="journalist">Journalist</option>
                          <option value="editor">Editor</option>
                          <option value="senior_editor">Senior Editor</option>
                          <option value="producer">Producer</option>
                          <option value="supervisor">Supervisor</option>
                          <option value="admin">Admin</option>
                          <option value="suspended">Suspended</option>
                        </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-zinc-500">{user.email}</td>
                    <td className="px-6 py-4 text-sm text-zinc-500 uppercase">{user.desk || '-'}</td>
                    <td className="px-6 py-4 text-sm text-zinc-500">
                      {user.last_login ? (
                        <div>
                          <div className="font-medium text-zinc-700 dark:text-zinc-300">
                            {new Date(user.last_login).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-zinc-400">
                            {formatRelativeTime(user.last_login)}
                          </div>
                        </div>
                      ) : (
                        <span className="text-zinc-400 italic">Never</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                       {!isSuspended && (
                         <button
                           onClick={() => handleDeleteUser(user.id, user.full_name || user.email || '')}
                           className="p-2 text-zinc-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-colors"
                           title="Suspend User"
                         >
                           <Trash2 className="h-4 w-4" />
                         </button>
                       )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* LOGIN HISTORY TAB */}
        {activeTab === 'loginHistory' && (
          <div className="flex flex-col">
            <div className="flex flex-wrap items-center gap-3 mb-3 px-6 pt-6">
              <select
                value={loginHistoryUserId}
                onChange={(e) => setLoginHistoryUserId(e.target.value)}
                className="px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
              >
                <option value="">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.full_name || user.email}
                  </option>
                ))}
              </select>
              <input
                type="date"
                value={loginHistoryFromDate}
                onChange={(e) => setLoginHistoryFromDate(e.target.value)}
                placeholder="From Date"
                className="px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
              />
              <input
                type="date"
                value={loginHistoryToDate}
                onChange={(e) => setLoginHistoryToDate(e.target.value)}
                placeholder="To Date"
                className="px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
              />
              <button
                onClick={() => {
                  setLoginHistoryUserId('');
                  setLoginHistoryFromDate('');
                  setLoginHistoryToDate('');
                }}
                className="text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-white px-3 py-2"
              >
                Clear Filters
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
                <thead className="bg-zinc-50 dark:bg-zinc-800/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Time</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Location</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                  {loginLogs.map(log => {
                    const location = log.new_values?.location as string | undefined;
                    return (
                      <tr key={log.id}>
                        <td className="px-6 py-3 text-sm text-zinc-500 whitespace-nowrap">
                          <div className="font-medium text-zinc-700 dark:text-zinc-300">
                            {new Date(log.created_at).toLocaleString()}
                          </div>
                          <div className="text-xs text-zinc-400">
                            {formatRelativeTime(log.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          {log.user?.full_name || log.user?.email || 'Unknown User'}
                        </td>
                        <td className="px-6 py-3 text-sm text-zinc-500">
                          {location || 'Location unknown'}
                        </td>
                      </tr>
                    );
                  })}
                  {loginLogs.length === 0 && (
                    <tr>
                      <td colSpan={3} className="px-6 py-8 text-center text-zinc-400 text-sm">
                        No login history found for this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* LOGS TAB */}
        {activeTab === 'logs' && (
          <div className="flex flex-col">
            <div className="flex flex-wrap items-center gap-3 mb-3 px-1">
              <input
                type="text"
                value={logSearch}
                onChange={(e) => setLogSearch(e.target.value)}
                placeholder="Search user, action, entity…"
                className="px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white placeholder-zinc-400"
              />
              <select
                value={logActionFilter}
                onChange={(e) => setLogActionFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white"
              >
                <option value="all">All actions</option>
                <option value="user_login">Logins</option>
                <option value="create_story">Create Story</option>
                <option value="update_story">Update Story</option>
                <option value="archive_story">Archive Story</option>
                <option value="create_terminology">Terminology</option>
                <option value="suspend_user">Suspend User</option>
              </select>
              <button
                onClick={() => { setLogSearch(''); setLogActionFilter('all'); }}
                className="text-sm text-zinc-500 hover:text-zinc-800 dark:hover:text-white px-3 py-2"
              >
                Clear
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
                <thead className="bg-zinc-50 dark:bg-zinc-800/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Time</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Action</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Entity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                  {filteredLogs.map(log => (
                    <tr key={log.id}>
                      <td className="px-6 py-3 text-sm text-zinc-500 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-3 text-sm font-medium text-zinc-900 dark:text-zinc-100">
                        {log.user?.full_name || log.user?.email || 'System'}
                      </td>
                      <td className="px-6 py-3 text-sm text-blue-600 dark:text-blue-400">{log.action}</td>
                      <td className="px-6 py-3 text-sm text-zinc-500 truncate max-w-md">
                        {log.entity_type ? `${log.entity_type}/${log.entity_id?.slice(0, 8)}` : '-'}
                      </td>
                    </tr>
                  ))}
                  {filteredLogs.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-8 text-center text-zinc-400 text-sm">
                        No activity found for this filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TERMINOLOGY TAB */}
        {activeTab === 'terms' && (
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-900/30 flex-1 mr-6">
                <h3 className="font-semibold text-blue-900 dark:text-blue-100 text-sm">How this works</h3>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                  Terms defined here are strictly enforced by the AI. Duplicate terms for the same language are prevented.
                </p>
              </div>
              <button
                onClick={() => {
                  setFormError(null);
                  setShowTermForm(true);
                }}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
              >
                <Plus className="h-4 w-4 mr-2" /> Add Term
              </button>
            </div>

            {showTermForm && (
              <div className="bg-zinc-50 dark:bg-zinc-800 p-5 rounded-lg border border-zinc-200 dark:border-zinc-700 mb-6 animate-fade-in animate-slide-in-up">
                {formError && (
                  <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 p-3 rounded-md flex items-center text-sm animate-fade-in animate-slide-in-down">
                    <AlertCircle className="h-4 w-4 mr-2" /> {formError}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Incorrect / Original Term</label>
                    <input
                      type="text"
                      value={newTerm.original || ''}
                      onChange={e => setNewTerm({...newTerm, original: e.target.value})}
                      className="w-full rounded-md border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 py-2 text-sm"
                      placeholder="e.g. Daneshgah"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Correct Replacement</label>
                    <input
                      type="text"
                      value={newTerm.replacement || ''}
                      onChange={e => setNewTerm({...newTerm, replacement: e.target.value})}
                      className="w-full rounded-md border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 py-2 text-sm"
                      placeholder="e.g. Pohantoon"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Language</label>
                    <select
                       value={newTerm.language}
                       onChange={e => setNewTerm({...newTerm, language: e.target.value})}
                       className="w-full rounded-md border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 py-2 text-sm"
                    >
                      {Object.entries(LANGUAGE_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-zinc-500 mb-1">Context / Notes</label>
                    <input
                      type="text"
                      value={newTerm.context || ''}
                      onChange={e => setNewTerm({...newTerm, context: e.target.value})}
                      className="w-full rounded-md border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 py-2 text-sm"
                      placeholder="e.g. Use Afghan terminology for University"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button onClick={() => setShowTermForm(false)} className="text-sm text-zinc-500 hover:text-zinc-900 px-3 py-2">Cancel</button>
                  <button onClick={handleSaveTerm} className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700">Save Rule</button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-zinc-200 dark:divide-zinc-800">
                <thead className="bg-zinc-50 dark:bg-zinc-800/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Original</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Replacement</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Language</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Created By</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-500 uppercase">Context</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-zinc-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                  {terms.map(term => (
                    <tr key={term.id} className="group hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                      <td className="px-6 py-4 text-sm text-red-500 line-through decoration-red-500/50">{term.original}</td>
                      <td className="px-6 py-4 text-sm font-bold text-green-600 dark:text-green-400">{term.replacement}</td>
                      <td className="px-6 py-4 text-sm text-zinc-500">{LANGUAGE_LABELS[term.language as LanguageCode] || term.language}</td>
                      <td className="px-6 py-4 text-sm text-zinc-500">
                        <div className="font-medium text-zinc-700 dark:text-zinc-300">{getUserName(term.createdBy)}</div>
                        <div className="text-xs text-zinc-400">{term.createdAt ? new Date(term.createdAt).toLocaleDateString() : '-'}</div>
                      </td>
                      <td className="px-6 py-4 text-sm text-zinc-500 italic">{term.context}</td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDeleteTerm(term.id)}
                          className="text-zinc-300 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {terms.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-10 text-center text-zinc-400 italic">No terminology rules defined yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PROMPTS TAB */}
        {activeTab === 'prompts' && (
          <div className="p-6">
            {/* Info Banner */}
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-100 dark:border-purple-900/30 mb-6">
              <h3 className="font-semibold text-purple-900 dark:text-purple-100 text-sm flex items-center">
                <Sparkles className="h-4 w-4 mr-2" /> AI Prompt Management
              </h3>
              <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                Manage and version AI agent prompts. Changes take effect immediately when activated. Previous versions are preserved for rollback.
              </p>
            </div>

            {/* Editing Panel */}
            {editingPrompt && (
              <div className="bg-zinc-50 dark:bg-zinc-800 p-4 rounded-lg border border-zinc-200 dark:border-zinc-700 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-zinc-900 dark:text-white flex items-center">
                    <Edit3 className="h-4 w-4 mr-2" />
                    Editing: {AGENT_INFO[editingPrompt]?.name || editingPrompt}
                  </h4>
                  <button
                    onClick={() => setEditingPrompt(null)}
                    className="text-zinc-400 hover:text-zinc-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
                    Prompt Text
                  </label>
                  <textarea
                    value={editPromptText}
                    onChange={(e) => setEditPromptText(e.target.value)}
                    rows={15}
                    className="w-full rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 py-2 text-sm font-mono text-zinc-900 dark:text-white"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">
                    Change Notes (optional)
                  </label>
                  <input
                    type="text"
                    value={editPromptNotes}
                    onChange={(e) => setEditPromptNotes(e.target.value)}
                    placeholder="e.g., Improved categorization for ai_fixable issues"
                    className="w-full rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-700 px-3 py-2 text-sm"
                  />
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setEditingPrompt(null)}
                    className="px-4 py-2 text-sm text-zinc-500 hover:text-zinc-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => savePromptVersion(false)}
                    disabled={savingPrompt}
                    className="px-4 py-2 text-sm bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 rounded-lg hover:bg-zinc-300 dark:hover:bg-zinc-600 disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 inline mr-1" />
                    Save as Draft
                  </button>
                  <button
                    onClick={() => savePromptVersion(true)}
                    disabled={savingPrompt}
                    className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    <Play className="h-4 w-4 inline mr-1" />
                    Save & Activate
                  </button>
                </div>
              </div>
            )}

            {/* Agent List */}
            <div className="space-y-3">
              {activePrompts.map((prompt) => {
                const info = AGENT_INFO[prompt.agent_name] || { name: prompt.agent_name, description: '', color: 'slate' };
                const isExpanded = expandedAgent === prompt.agent_name;
                const versions = agentVersions[prompt.agent_name] || [];

                return (
                  <div
                    key={prompt.id}
                    className="border border-zinc-200 dark:border-zinc-700 rounded-lg overflow-hidden"
                  >
                    {/* Agent Header */}
                    <div className="bg-zinc-50 dark:bg-zinc-800/50 p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <button
                            onClick={() => loadAgentVersions(prompt.agent_name)}
                            className="p-1 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded mr-2"
                          >
                            {loadingVersions === prompt.agent_name ? (
                              <div className="h-4 w-4 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
                            ) : isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-zinc-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-zinc-500" />
                            )}
                          </button>
                          <div>
                            <h4 className="font-semibold text-zinc-900 dark:text-white flex items-center">
                              <span className={`w-2 h-2 rounded-full bg-${info.color}-500 mr-2`} />
                              {info.name}
                              <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                                v{prompt.version} active
                              </span>
                            </h4>
                            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                              {info.description}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setViewingPrompt(prompt)}
                            className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded-lg"
                            title="View prompt"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => startEditingPrompt(prompt)}
                            className="p-2 text-zinc-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                            title="Edit (create new version)"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Last updated info */}
                      <div className="mt-2 flex items-center text-xs text-zinc-400">
                        <Clock className="h-3 w-3 mr-1" />
                        Activated {prompt.activated_at ? new Date(prompt.activated_at).toLocaleDateString() : 'at creation'}
                        {prompt.notes && (
                          <span className="ml-3 italic">"{prompt.notes}"</span>
                        )}
                      </div>
                    </div>

                    {/* Version History */}
                    {isExpanded && (
                      <div className="border-t border-zinc-200 dark:border-zinc-700">
                        <div className="p-3 bg-zinc-100/50 dark:bg-zinc-900/50">
                          <h5 className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider flex items-center">
                            <History className="h-3 w-3 mr-1" /> Version History
                          </h5>
                        </div>
                        <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
                          {versions.map((v) => (
                            <div
                              key={v.id}
                              className={`p-3 flex items-center justify-between hover:bg-zinc-50 dark:hover:bg-zinc-800/50 ${
                                v.is_active ? 'bg-green-50/50 dark:bg-green-900/10' : ''
                              }`}
                            >
                              <div className="flex items-center">
                                <span className={`font-mono text-sm font-bold mr-3 ${
                                  v.is_active ? 'text-green-600 dark:text-green-400' : 'text-zinc-400'
                                }`}>
                                  v{v.version}
                                </span>
                                <div>
                                  <div className="text-sm text-zinc-700 dark:text-zinc-300">
                                    {v.notes || 'No notes'}
                                    {v.is_active && (
                                      <span className="ml-2 text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded">
                                        ACTIVE
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-zinc-400">
                                    Created {new Date(v.created_at).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => setViewingPrompt(v)}
                                  className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-700 rounded"
                                  title="View"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                                {!v.is_active && (
                                  <button
                                    onClick={() => handleActivatePrompt(v.id, v.agent_name)}
                                    className="p-1.5 text-zinc-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                    title="Activate this version"
                                  >
                                    <RotateCcw className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {activePrompts.length === 0 && (
                <div className="text-center py-10 text-zinc-400">
                  <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No prompts found. Run the database migration to seed initial prompts.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* View Prompt Modal */}
      {viewingPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-sm w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col border border-zinc-200 dark:border-zinc-800">
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-zinc-900 dark:text-white">
                  {AGENT_INFO[viewingPrompt.agent_name]?.name || viewingPrompt.agent_name}
                </h3>
                <p className="text-xs text-zinc-500">
                  Version {viewingPrompt.version} {viewingPrompt.is_active && '(Active)'}
                </p>
              </div>
              <button
                onClick={() => setViewingPrompt(null)}
                className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <pre className="text-sm font-mono whitespace-pre-wrap text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-800 p-4 rounded-lg">
                {viewingPrompt.prompt_text}
              </pre>
            </div>
            <div className="p-4 border-t border-zinc-200 dark:border-zinc-800 flex justify-between items-center">
              <div className="text-xs text-zinc-400">
                {viewingPrompt.notes && <span className="italic">"{viewingPrompt.notes}"</span>}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(viewingPrompt.prompt_text);
                  }}
                  className="px-3 py-1.5 text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg"
                >
                  <Copy className="h-4 w-4 inline mr-1" /> Copy
                </button>
                <button
                  onClick={() => {
                    startEditingPrompt(viewingPrompt);
                    setViewingPrompt(null);
                  }}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Edit3 className="h-4 w-4 inline mr-1" /> Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
